import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { ArrowLeft, ChevronLeft, ChevronRight, History as HistoryIcon, Search, Wallet, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import StatusChip from '../../component/common/StatusChip';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import { MobileCashHistory } from '../../component/cashregister/MobileCashRegisters';
import { CASH_EVENT_META, eventToneColors, formatBRMoney, parseBRMoney } from './shared';

// Histórico do caixa — timeline vertical (padrão novo de UI, sem protótipo
// anterior — documentado como extensão do DESIGN-SYSTEM.md §4/§5, ver
// DESIGN-SYSTEM.md "§5.19 Timeline vertical" e domínio "Evento de caixa").
// Ref: `prototipo-historico-caixa.html`.
//
// Fonte real: `GET /cashregister/{id}/history`
// (`cashregister.CashRegisterHistoryResponse[]`, já ordenado
// `date_operation DESC` pelo backend). O campo "Motivo"/observação só existe
// de fato para sangrias (`reason_bleeding`) — abertura/fechamento não têm
// nenhum campo de observação no schema (`cash_registers_history` não grava
// nada além do operador/valor para esses 2 tipos), então o card de
// Abertura/Fechamento nunca mostra a linha opcional de observação.
export default function CashRegisterHistory() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();

    const [loading, setLoading] = useState(true);
    const [cash, setCash] = useState(null);
    useAppBarTitle(isMobileShell && cash ? `Histórico · ${cash.name}` : '');
    const [status, setStatus] = useState('');
    const [events, setEvents] = useState([]);

    const [draftType, setDraftType] = useState('');
    const [draftSort, setDraftSort] = useState('desc');
    const [draftSearch, setDraftSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [sort, setSort] = useState('desc');
    const [search, setSearch] = useState('');

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [dataRes, resumeRes, historyRes] = await Promise.all([
                    httpClient.get(`/cashregister/${id}`),
                    httpClient.get(`/cashregister/${id}/resume`).catch(() => ({ data: {} })),
                    httpClient.get(`/cashregister/${id}/history`),
                ]);
                setCash(dataRes.data);
                setStatus(resumeRes.data?.details?.status || '');
                setEvents(Array.isArray(historyRes.data) ? historyRes.data : []);
            } catch (error) {
                console.error('Erro ao carregar histórico do caixa:', error);
                setEvents([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const filtered = useMemo(() => {
        let list = events.filter((ev) => {
            if (typeFilter && ev.TypeOperation !== typeFilter) return false;
            if (search) {
                const term = search.toLowerCase();
                // Shell mobile: a busca também casa a data ("Buscar por data…").
                const haystack = `${ev.User || ''} ${ev.ReasonBleending || ''} ${CASH_EVENT_META[ev.TypeOperation]?.label || ''}${isMobileShell ? ` ${ev.DateOperationFormatted || ''}` : ''}`.toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            return true;
        });
        list = [...list].sort((a, b) => {
            const diff = new Date(a.DateOperation).getTime() - new Date(b.DateOperation).getTime();
            return sort === 'asc' ? diff : -diff;
        });
        return list;
    }, [events, typeFilter, search, sort, isMobileShell]);

    const totalSangrias = filtered
        .filter((ev) => ev.TypeOperation === 'cash_bleeding')
        .reduce((sum, ev) => sum + parseBRMoney(ev.Amount), 0);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const effectivePage = Math.min(page, totalPages);
    const pageItems = filtered.slice((effectivePage - 1) * pageSize, effectivePage * pageSize);

    const applyFilters = () => {
        setTypeFilter(draftType);
        setSort(draftSort);
        setSearch(draftSearch.trim());
        setPage(1);
    };
    const clearFilters = () => {
        setDraftType('');
        setDraftSort('desc');
        setDraftSearch('');
        setTypeFilter('');
        setSort('desc');
        setSearch('');
        setPage(1);
    };

    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const typeLabel = typeFilter ? CASH_EVENT_META[typeFilter]?.label : 'Todos';

    // Shell mobile: histórico do protótipo `caixas/historico.html` (mesmos dados, busca, tipo e ordem).
    if (isMobileShell) {
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget="Histórico do caixa">
                <MobileCashHistory
                    loading={loading}
                    events={filtered}
                    eventLabels={{ open: CASH_EVENT_META.open.label, cash_bleeding: CASH_EVENT_META.cash_bleeding.label, closed: CASH_EVENT_META.closed.label }}
                    search={draftSearch}
                    onSearchChange={(value) => { setDraftSearch(value); setSearch(value.trim()); setPage(1); }}
                    type={draftType}
                    onTypeChange={setDraftType}
                    sort={draftSort}
                    onSortChange={setDraftSort}
                    onApplyFilters={applyFilters}
                    onClearFilters={clearFilters}
                    filtersActive={Boolean(typeFilter) || sort !== 'desc'}
                />
            </PageScaffold>
        );
    }

    if (loading) {
        return (
            <PageScaffold links={[]} titlePageTarget="Histórico do caixa">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress size={40} /></Box>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget="Histórico do caixa">
            <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                            <HistoryIcon size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Histórico do Caixa
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Linha do tempo de aberturas, fechamentos e sangrias deste terminal.
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowLeft size={15} strokeWidth={2} />} onClick={() => navigate(`/cashregisters/view/${id}`)}>
                        Voltar ao painel
                    </Button>
                </Box>

                {/* ctx-strip */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', p: '14px 18px', mb: '18px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: (t) => `${t.shape.borderRadius}px`, boxShadow: (t) => t.customShadows?.sm, flexWrap: 'wrap' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '11px', bgcolor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Wallet size={18} strokeWidth={2} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{cash?.name}</Typography>
                    </Box>
                    <StatusChip domain="cashRegisterStatus" value={status === 'open' ? 'open' : 'closed'} />
                </Box>

                {/* report-toolbar */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, p: 2, mb: '14px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: (t) => `${t.shape.borderRadius}px` }}>
                    <Box>
                        <TbLabel>Tipo de evento</TbLabel>
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                            <Select
                                value={draftType}
                                displayEmpty
                                onChange={(e) => setDraftType(e.target.value)}
                                renderValue={() => (draftType ? CASH_EVENT_META[draftType]?.label : 'Todos')}
                                sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="open">Abertura</MenuItem>
                                <MenuItem value="closed">Fechamento</MenuItem>
                                <MenuItem value="cash_bleeding">Sangria</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <TbLabel>Ordenar por</TbLabel>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                                value={draftSort}
                                onChange={(e) => setDraftSort(e.target.value)}
                                sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                            >
                                <MenuItem value="desc">Mais recentes primeiro</MenuItem>
                                <MenuItem value="asc">Mais antigos primeiro</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ flex: '0 0 240px' }}>
                        <TbLabel>Buscar</TbLabel>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Operador, motivo ou observação…"
                            value={draftSearch}
                            onChange={(e) => setDraftSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search size={15} strokeWidth={2} /></InputAdornment>,
                                sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" size="small" onClick={clearFilters} sx={{ height: '33px' }}>Limpar</Button>
                        <Button variant="contained" size="small" startIcon={<Search size={14} strokeWidth={2} />} onClick={applyFilters} sx={{ height: '33px' }}>Buscar</Button>
                    </Stack>
                </Box>

                {/* chips-row */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, rowGap: 1 }}>
                    {typeFilter && (
                        <Chip label={`Tipo: ${typeLabel}`} size="small" onDelete={() => { setTypeFilter(''); setDraftType(''); }} deleteIcon={<X size={12} strokeWidth={2.5} />} sx={{ bgcolor: 'info.bg', color: 'primary.main' }} />
                    )}
                    {search && (
                        <Chip label={`Busca: "${search}"`} size="small" onDelete={() => { setSearch(''); setDraftSearch(''); }} deleteIcon={<X size={12} strokeWidth={2.5} />} sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }} />
                    )}
                    <Chip label={`${filtered.length} evento${filtered.length === 1 ? '' : 's'} encontrado${filtered.length === 1 ? '' : 's'}`} size="small" sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }} />
                    <Chip label={`Total em sangrias: R$ ${formatBRMoney(totalSangrias)}`} size="small" sx={{ bgcolor: 'error.bg', color: 'error.main' }} />
                </Stack>

                {/* TIMELINE */}
                <Box sx={{ p: '18px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: (t) => `${t.shape.borderRadius}px`, boxShadow: (t) => t.customShadows?.sm }}>
                    {pageItems.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1, color: 'text.disabled', textAlign: 'center' }}>
                            <Box sx={{ width: 64, height: 64, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Search size={26} strokeWidth={1.6} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.primary' }}>Nenhum evento encontrado</Typography>
                            <Typography sx={{ fontSize: 12.5, maxWidth: 320 }}>Ajuste o tipo de evento ou o termo buscado para ver outros registros deste caixa.</Typography>
                            <Button variant="outlined" size="small" onClick={clearFilters}>Limpar filtros</Button>
                        </Box>
                    ) : (
                        <Box sx={{ position: 'relative', p: '6px 4px 4px' }}>
                            {pageItems.map((ev, idx) => {
                                const meta = CASH_EVENT_META[ev.TypeOperation] || CASH_EVENT_META.closed;
                                const { bg, fg } = eventToneColors(theme, meta.tone);
                                const Icon = meta.icon;
                                const isLast = idx === pageItems.length - 1;
                                const amountColor = meta.tone === 'danger' ? 'error.main' : meta.tone === 'success' ? 'success.main' : 'text.primary';
                                const prefix = ev.TypeOperation === 'cash_bleeding' ? '− ' : '';
                                return (
                                    <Box key={idx} sx={{ position: 'relative', pl: '54px', pb: isLast ? 0 : '24px' }}>
                                        {!isLast && (
                                            <Box sx={{ position: 'absolute', left: '19px', top: '40px', bottom: '-24px', width: '2px', bgcolor: 'divider' }} />
                                        )}
                                        <Box sx={{ position: 'absolute', left: 0, top: 0, width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: bg, color: fg, boxShadow: (t) => `0 0 0 4px ${t.palette.background.paper}` }}>
                                            <Icon size={17} strokeWidth={2} />
                                        </Box>
                                        <Box sx={{ bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '14px', p: '14px 16px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', mb: '2px' }}>
                                                <Chip label={meta.label} size="small" sx={{ bgcolor: bg, color: fg }} />
                                                <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 700, whiteSpace: 'nowrap' }}>{ev.DateOperationFormatted}</Typography>
                                            </Box>
                                            <Typography sx={{ fontWeight: 800, fontSize: 16, mt: '2px', color: amountColor }}>{prefix}R$ {ev.Amount}</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', mt: '10px', pt: '10px', borderTop: '1px solid', borderColor: 'divider' }}>
                                                <Box>
                                                    <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled', fontWeight: 700, mb: '2px' }}>Operador</Typography>
                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{ev.User}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled', fontWeight: 700, mb: '2px' }}>{meta.amountLabel}</Typography>
                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>R$ {ev.Amount}</Typography>
                                                </Box>
                                            </Box>
                                            {ev.ReasonBleending && (
                                                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1, lineHeight: 1.45 }}>{ev.ReasonBleending}</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {/* pagination */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', mt: '18px', pt: '16px', borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            Mostrando <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{filtered.length === 0 ? 0 : (effectivePage - 1) * pageSize + 1}–{Math.min(effectivePage * pageSize, filtered.length)}</Box> de <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{filtered.length}</Box> eventos
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.04em' }}>Por página</Typography>
                            <FormControl size="small">
                                <Select value={pageSize} onChange={(e) => { setPageSize(e.target.value); setPage(1); }} sx={{ height: 34, fontSize: '12.5px', borderRadius: '8px' }}>
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={6}>6</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                </Select>
                            </FormControl>
                            <Button
                                size="small"
                                disabled={effectivePage <= 1}
                                onClick={() => setPage((p) => p - 1)}
                                sx={{ minWidth: 38, width: 38, height: 38, borderRadius: '10px', border: '1px solid', borderColor: 'divider', color: 'text.secondary' }}
                            >
                                <ChevronLeft size={16} strokeWidth={2} />
                            </Button>
                            <Typography sx={{ fontSize: '12.5px', fontWeight: 700 }}>Página {effectivePage} de {totalPages}</Typography>
                            <Button
                                size="small"
                                disabled={effectivePage >= totalPages}
                                onClick={() => setPage((p) => p + 1)}
                                sx={{ minWidth: 38, width: 38, height: 38, borderRadius: '10px', border: '1px solid', borderColor: 'divider', color: 'text.secondary' }}
                            >
                                <ChevronRight size={16} strokeWidth={2} />
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </PageScaffold>
    );
}
